import React, { useEffect, useRef, useState } from "react";
import "./CreateAnnouncementPost.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CommonEnums } from "../../../../enums/common.enums";
import { TemplatesApi } from "../../../../utils/api/Templates";
import toast from "react-hot-toast";
import { clientRoutes } from "../../../../utils/routes";
import { Modal } from "@mui/material";
import { CommonHelpers } from "../../../../utils/helpers/common.helpers";
import PreviewModal from "../propertypost/view/PreviewModal";
import UpdateTemplateModal from "../../WorkspaceSheduleConfigure/UpdateTemplateModal";
import Loading from "../../../loading/Loading";
import { PropertyApi } from "../../../../utils/api/Property";
import AnnouncementPostTabs from "./AnnouncementPostTabs";
import Upload from "../../../../components/icons/Upload";
import AnnouncementConfigurePost from "./AnnouncementConfigurePost";
import AnnouncementPostType from "./AnnouncementPostType";
import AnnouncementType from "./AnnouncementType";
import SelectTemplatesList from "./SelectTemplate";
import clsx from "clsx";
import imageCompression from "browser-image-compression";
import { AuthHelpers } from "../../../../utils/auth.helpers";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const AgencyCreateAnnouncementPost = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = searchParams.get("step") || "post_type";
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPostType, setSelectedPostType] = useState<string | null>(null);
  const [selectedPostTypeTitle, setSelectedPostTypeTitle] =
    useState<string>("N/A");

  const postTypeFromUrl = searchParams.get("post_type");
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [inputs, setInputs] = useState<{ [key: string]: any }>({});
  const [announcementPost, setAnnouncementPost] = useState<any>(null);
  const announcementPostId = searchParams.get("announcement_post_id") ?? "";
  const [isGenerating, setIsGenerating] = useState(false);

  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [initialFormValues, setInitialFormValues] = useState<{
    [key: string]: any;
  }>({});
  const [inputOrder, setInputOrder] = useState<string[]>([]);
  const [imageEditing, setImageEditing] = useState<{ [key: string]: boolean }>(
    {}
  );
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [draggedOverKey, setDraggedOverKey] = useState<string | null>(null);

  const handleFileUpdate = (file: File | undefined, key: string) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select or drop a valid image file.");
      setDraggedOverKey(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setFormValues((prev) => ({
      ...prev,
      [key]: { ...prev[key], value: url, file, original_path: "" },
    }));

    setImageEditing((prev) => ({ ...prev, [key]: false }));
    setDraggedOverKey(null);
  };

  const fetchTemplate = async () => {
    setIsLoading(true);
    try {
      const announcementPostId = searchParams.get("announcement_post_id");
      const templateIdFromUrl = searchParams.get("template_id");
      const userRole = AuthHelpers.getUserType();
      const postType = searchParams.get("post_type");

      if (!templateIdFromUrl) {
        toast.error("Template ID is missing. Cannot load inputs.");
        setIsLoading(false);
        return;
      }

      const res = await TemplatesApi.getDesignTemplateById(templateIdFromUrl);

      if (res.success && res.data) {
        const templateDetails = res.data.data;
        setTemplateData({ data: templateDetails });

        let rolePostTypeKey = "";
        if (userRole === "agent") {
          rolePostTypeKey =
            postType === "image_post"
              ? "agent_image_template_inputs"
              : "agent_story_template_inputs";
        } else {
          rolePostTypeKey =
            postType === "image_post"
              ? "agency_image_template_inputs"
              : "agency_story_template_inputs";
        }

        const apiInputs = templateDetails[rolePostTypeKey] || {};
        setInputs(apiInputs);
        setInputOrder(Object.keys(apiInputs));

        let announcementData = null;
        if (announcementPostId) {
          const announcementRes = await PropertyApi.GetAnnouncementPostById(
            announcementPostId
          );
          announcementData = announcementRes?.data?.data;
          setAnnouncementPost(announcementData);
        }

        const existingInputs = announcementData?.inputs || {};
        const newFormValues: { [key: string]: any } = {};

        Object.keys(apiInputs).forEach((key, index) => {
          const apiInput = apiInputs[key];
          const existingValue = existingInputs[key];
          const isImage = apiInput.input_type === "image";

          let value: any = "";
          let original_path: string | null = null;

          if (isImage) {
            original_path =
              existingValue?.filepath ?? apiInput.filepath ?? null;
            if (original_path) {
              value = CommonHelpers.getImageUrlFromImageTool({
                path: original_path,
              });
            }
          } else {
            value = existingValue?.value ?? apiInput.value ?? "";
          }

          newFormValues[key] = {
            value: value,
            original_path: original_path,
            file: null,
            handler: apiInput.handler || key,
            label: apiInput.label || key,
            template_input_index: apiInput.template_input_index ?? index,
            characters_limit: apiInput.characters_limit || 100,
            input_type: apiInput.input_type || "text",
            enabled: existingValue?.enabled ?? apiInput.enabled ?? true,
            title_input: apiInput.title_input ?? false,
            use_main_image: isImage ? apiInput.use_main_image ?? false : false,
          };
        });

        setFormValues(newFormValues);
        setInitialFormValues(JSON.parse(JSON.stringify(newFormValues)));

        const existingGeneratedImagePath =
          announcementData?.image_post_design_activity_ref?.design_task_id
            ?.output_image_path ||
          announcementData?.story_post_design_activity_ref?.design_task_id
            ?.output_image_path;

        if (existingGeneratedImagePath) {
          const assetUrl = CommonHelpers.getImageUrlFromImageTool({
            path: existingGeneratedImagePath,
          });
          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));
        } else if (announcementPostId) {
          setIsGenerating(true);
          const pollInterval = 3000;

          const pollDesignStatus = async () => {
            try {
              const check = await PropertyApi.GetAnnouncementPostById(
                announcementPostId
              );
              const updatedData = check?.data?.data;
              const refs = [
                updatedData?.image_post_design_activity_ref,
                updatedData?.story_post_design_activity_ref,
              ].filter(Boolean);

              const firstWithImage = refs.find(
                (ref) => ref?.design_task_id?.output_image_path
              );

              if (firstWithImage) {
                const assetUrl = CommonHelpers.getImageUrlFromImageTool({
                  path: firstWithImage.design_task_id.output_image_path,
                });
                setTemplateData((prev: any) => ({
                  ...prev,
                  generatedImage: assetUrl,
                }));
                setIsGenerating(false);
                toast.success("Design ready!");
              } else {
                setTimeout(pollDesignStatus, pollInterval);
              }
            } catch (err) {
              console.error("Polling error:", err);
              setIsGenerating(false);
              toast.error("Error checking design status.");
            }
          };

          pollDesignStatus();
        }
      } else {
        toast.error(res.error || "Failed to fetch template details!");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while fetching the template!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (step === CommonEnums.announcementPostTabs.create_post.key) {
      fetchTemplate();
    }
  }, [
    step,
    searchParams.get("announcement_post_id"),
    searchParams.get("template_id"),
  ]);

  const buildInputsPayload = async () => {
    const imageKeysWithFiles = Object.keys(formValues).filter(
      (key) => formValues[key]?.input_type === "image" && formValues[key]?.file
    );

    const uploadedFilePaths: Record<string, string> = {};

    if (imageKeysWithFiles.length > 0) {
      const uploadEndpoint = `${process.env.REACT_APP_DESIGN_TOOL_ENDPOINT}/api/uploads/upload-template-input-images`;
      const formData = new FormData();

      const processInputImage = async (key: string) => {
        console.log("Processing image with key:", key);
        const fileObj = formValues[key].file;
        if (!fileObj) return;

        const imageInputData = formValues[key];
        const inputLabel = imageInputData?.label || key;
        const filename = fileObj.name;
        const mimetype = fileObj.type;

        const options = {
          // You can tune these if needed
          // maxSizeMB: 1,
          // maxWidthOrHeight: 1024,
          useWebWorker: true,
          fileType: mimetype,
        };

        try {
          const compressedFile = await imageCompression(fileObj, options);

          if (compressedFile) {
            formData.append("input_image", compressedFile, filename);
          }
        } catch (error) {
          console.error("Error during compression or upload:", error);
          toast.error(`Image upload failed for: ${inputLabel}`);
        }

        console.log("Completed process for image:", key);
      };

      await Promise.all(
        imageKeysWithFiles.map((key) => processInputImage(key))
      );

      try {
        const response = await fetch(uploadEndpoint, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();
        console.log("Upload response:", data);

        if (
          data?.success &&
          Array.isArray(data.data) &&
          data.data.length === imageKeysWithFiles.length
        ) {
          imageKeysWithFiles.forEach((key, index) => {
            const filePathData = data.data[index];
            const path = filePathData?.filepath || filePathData?.path;
            if (path) {
              uploadedFilePaths[key] = path;
            }
          });
          console.log("✅ Uploaded filepaths:", uploadedFilePaths);
        } else {
          throw new Error("Bulk image upload failed.");
        }
      } catch (uploadErr) {
        console.error("Image upload failed:", uploadErr);
        toast.error("Failed to upload image(s). Please try again.");
        return null;
      }
    }

    const inputsPayload: Record<string, any> = {};
    Object.keys(formValues).forEach((key) => {
      const val = formValues[key];
      if (!val) return;

      const originalInput = inputs[key];
      if (!originalInput) return;

      const isImage = val.input_type === "image";

      if (isImage) {
        const hasNewFile = !!val.file;
        const imagePayload: any = {
          ...originalInput,
          handler: val.handler || key,
          label: val.label || key,
          template_input_index: val.template_input_index ?? 0,
          input_type: "image",
          enabled: val.enabled ?? true,
          use_main_image:
            !hasNewFile && (originalInput.use_main_image ?? false),
        };

        if (hasNewFile) {
          imagePayload.filepath = uploadedFilePaths[key] || "";
        } else {
          imagePayload.filepath = val.original_path || "";
        }

        delete imagePayload.value;
        delete imagePayload.file;
        delete imagePayload.original_path;

        inputsPayload[key] = imagePayload;
      } else {
        const initialValue = initialFormValues[key]?.value || "";
        const currentValue = val.value || "";

        // let useStageNameValue;
        // if (originalInput.title_input === true) {
        //   useStageNameValue = currentValue === initialValue;
        // } else {
        //   useStageNameValue = false;
        // }

        inputsPayload[key] = {
          ...originalInput,
          handler: val.handler || key,
          label: val.label || key,
          value: currentValue,
          template_input_index: val.template_input_index ?? 0,
          characters_limit: val.characters_limit ?? 100,
          input_type: val.input_type || "text",
          enabled: val.enabled ?? true,
          // use_stage_name: useStageNameValue,
        };
      }
    });

    return inputsPayload;
  };

  const handleCreateOnboardingPost = async () => {
    if (!announcementPostId) {
      toast.error("Missing announcement post ID in URL!");
      return;
    }
    setProcessingModalOpen(true);
    try {
      const inputsPayload = await buildInputsPayload();
      if (!inputsPayload) {
        setProcessingModalOpen(false);
        return;
      }

      const res = await PropertyApi.updateAnnouncementPost(
        { inputs: inputsPayload, finalize: true },
        announcementPostId
      );

      if (res?.data?.success) {
        toast.success("Onboarding post updated successfully!");
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("step", CommonEnums.announcementPostTabs.configure_post.key);
          return next;
        });
      } else {
        toast.error(res?.data?.message || "Failed to update onboarding post!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while updating onboarding post!");
    } finally {
      setProcessingModalOpen(false);
    }
  };

  const handleUpdatedesign = async () => {
    const announcementPostId = searchParams.get("announcement_post_id");
    const templateId = searchParams.get("template_id");

    if (!announcementPostId || !templateId) {
      toast.error("Post ID or Template ID is missing!");
      return;
    }

    setProcessingModalOpen(true);
    try {
      const inputsPayload = await buildInputsPayload();
      if (!inputsPayload) {
        setProcessingModalOpen(false);
        return;
      }

      const payload = {
        inputs: inputsPayload,
        finalize: true,
      };

      const res = await PropertyApi.updateAnnouncementPost(
        payload,
        announcementPostId
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Internal server error!");
        setProcessingModalOpen(false);
        return;
      }
      setProcessingModalOpen(false);
      setIsGenerating(true);

      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetAnnouncementPostById(
            announcementPostId
          );
          const updatedData = check?.data?.data;
          const refs = [
            updatedData?.image_post_design_activity_ref,
            updatedData?.story_post_design_activity_ref,
          ].filter(Boolean);
          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );
          if (firstWithImage) {
            const assetUrl = CommonHelpers.getImageUrlFromImageTool({
              path: firstWithImage.design_task_id.output_image_path,
            });
            setTemplateData((prev: any) => ({
              ...prev,
              generatedImage: assetUrl,
            }));
            setIsGenerating(false);
            toast.success("Design generated successfully!");
          } else {
            setTimeout(pollDesignStatus, pollInterval);
          }
        } catch (err) {
          console.error("Polling error:", err);
          setIsGenerating(false);
          toast.error("Error checking design status.");
        }
      };
      pollDesignStatus();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
      setProcessingModalOpen(false);
    }
  };

  const resetInputs = () => window.location.reload();

  const getPreviewContainerClass = (postType: string | null) => {
    switch (postType) {
      case CommonEnums.postType.story_post.key:
        return "story-post-preview";
      default:
        return "image-post-preview";
    }
  };

  const getPreviewImage = () => {
    if (templateData?.generatedImage) {
      return templateData.generatedImage;
    }
    if (
      announcementPost?.image_post_design_activity_ref?.design_task_id
        ?.output_image_path
    ) {
      return CommonHelpers.getImageUrlFromImageTool({
        path: announcementPost.image_post_design_activity_ref.design_task_id
          .output_image_path,
      });
    }
    if (
      announcementPost?.story_post_design_activity_ref?.design_task_id
        ?.output_image_path
    ) {
      return CommonHelpers.getImageUrlFromImageTool({
        path: announcementPost.story_post_design_activity_ref.design_task_id
          .output_image_path,
      });
    }
    if (templateData?.data?.template_image) {
      return `${API_BASE_URL}/${templateData.data.template_image}`;
    }
    return "/images/image-video-generation-error-fallback-image-for-card.png";
  };

  const handleSendEmptyAnnouncementUpdate = async () => {
    const announcementPostId = searchParams.get("announcement_post_id");

    if (!announcementPostId) {
      toast.error("Announcement Post ID missing in URL!");
      return;
    }

    try {
      setProcessingModalOpen(true);

      const payload = {
        inputs: {},
        image_post_design_activity_ref: null,
        story_post_design_activity_ref: null,
        video_post_design_activity_ref: null,
        reel_post_design_activity_ref: null,
      };

      const res = await PropertyApi.updateAnnouncementPost(
        payload,
        announcementPostId
      );

      if (res?.data?.success) {
      } else {
        toast.error(res?.data?.message || "Failed to update announcement!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    } finally {
      setProcessingModalOpen(false);
    }
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="template-inputs-wrapper">
          <div className="top-header-bar">
            <div
              className="agent-view-back-btn"
              onClick={() => {
                const subscriptionType = searchParams.get("subscription_type");
                const backUrl = subscriptionType
                  ? `${clientRoutes.AgencyAnnouncementPostList}?subscription_type=${subscriptionType}`
                  : clientRoutes.AgencyAnnouncementPostList;
                navigate(backUrl);
              }}
            >
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <p className="bakc-text-contain">Back to List</p>
            </div>
            <div className="page-title">Create Announcement Post</div>
          </div>

          <AnnouncementPostTabs
            activeTab={step}
            onTabChange={(newStep) =>
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("step", newStep);
                return next;
              })
            }
          />

          {step === CommonEnums.announcementPostTabs.create_post.key && (
            <>
              {isLoading ? (
                <div className="list-page-loading">
                  <Loading />
                </div>
              ) : (
                <div className="template-inputs-grid">
                  <div className="template-input-fields">
                    <div className="section-heading">Inputs</div>
                    <div className="input-description">
                      Feel free to update the inputs to customize the onboarding
                      post
                    </div>

                    {inputOrder
                      .filter((key) => formValues[key])
                      .map((key) => {
                        const formValueItem = formValues[key];
                        if (!formValueItem) return null;

                        const isImage = formValueItem.input_type === "image";
                        const isTitleInput = formValueItem.title_input ?? false;
                        const isEnabled = formValueItem.enabled ?? true;

                        const value = formValueItem.value || "";
                        const charLimit = formValueItem.characters_limit || 100;
                        const isEditing = imageEditing[key] || false;

                        if (isImage) {
                          return (
                            <div className="input-field-block" key={key}>
                              <div className="image-block">
                                <div className="employee-image-header">
                                  <label className="employee-input-label">
                                    {formValueItem.label}
                                  </label>
                                  {!isEditing ? (
                                    <button
                                      className="change-image-btn"
                                      type="button"
                                      disabled={isGenerating}
                                      onClick={() =>
                                        !isGenerating &&
                                        setImageEditing((p) => ({
                                          ...p,
                                          [key]: true,
                                        }))
                                      }
                                    >
                                      Change Image
                                    </button>
                                  ) : (
                                    <button
                                      className="cancel-image-btn"
                                      type="button"
                                      disabled={isGenerating}
                                      onClick={() =>
                                        !isGenerating &&
                                        setImageEditing((p) => ({
                                          ...p,
                                          [key]: false,
                                        }))
                                      }
                                    >
                                      Cancel
                                    </button>
                                  )}
                                </div>
                                {!isEditing && value && (
                                  <div className="employee-image-preview">
                                    <img
                                      src={value}
                                      alt={formValueItem.label}
                                      className="employee-image"
                                    />
                                  </div>
                                )}
                                {isEditing && !isGenerating && (
                                  <div
                                    className={clsx("employee-image-upload", {
                                      "drag-over": draggedOverKey === key,
                                    })}
                                    onClick={() =>
                                      fileInputRefs.current[key]?.click()
                                    }
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleFileUpdate(
                                        e.dataTransfer.files?.[0],
                                        key
                                      );
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onDragEnter={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDraggedOverKey(key);
                                    }}
                                    onDragLeave={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (
                                        !e.currentTarget.contains(
                                          e.relatedTarget as Element
                                        )
                                      ) {
                                        setDraggedOverKey(null);
                                      }
                                    }}
                                  >
                                    <input
                                      type="file"
                                      accept="image/*"
                                      ref={(el) =>
                                        (fileInputRefs.current[key] = el)
                                      }
                                      style={{ display: "none" }}
                                      onChange={(e) =>
                                        handleFileUpdate(
                                          e.target.files?.[0],
                                          key
                                        )
                                      }
                                    />
                                    <div className="upload-icon">
                                      <Upload />
                                    </div>
                                    <p>Drop or Select file</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div className="input-field-block" key={key}>
                            <div className="input-label-row">
                              <label className="input-label">
                                {formValueItem.label} ({value.length}/
                                {charLimit})
                              </label>
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  disabled={isGenerating}
                                  onChange={(e) =>
                                    !isGenerating &&
                                    setFormValues((prev) => ({
                                      ...prev,
                                      [key]: {
                                        ...prev[key],
                                        enabled: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                                <span className="slider round"></span>
                              </label>
                            </div>
                            <textarea
                              className="input-textarea"
                              placeholder={formValueItem.label}
                              maxLength={charLimit}
                              value={value}
                              disabled={isGenerating || !isEnabled}
                              onChange={(e) =>
                                !isGenerating &&
                                setFormValues((prev) => ({
                                  ...prev,
                                  [key]: {
                                    ...prev[key],
                                    value: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        );
                      })}

                    <div className="action-text-buttons">
                      <span
                        className={clsx("text-button", {
                          disabled: isGenerating,
                        })}
                        onClick={() => !isGenerating && resetInputs()}
                      >
                        Reset Inputs
                      </span>
                      <span
                        className={clsx("text-button", {
                          disabled: isGenerating,
                        })}
                        onClick={() => !isGenerating && handleUpdatedesign()}
                      >
                        Update & Generate New Design
                      </span>
                    </div>
                  </div>

                  <div className="template-preview">
                    <div className="section-heading">Post Preview</div>
                    <div className="template-preview-label">
                      <span>Preview of design template</span>
                      <div className="preview-actions">
                        <span
                          className={clsx("preview-link", {
                            disabled: isGenerating,
                          })}
                          onClick={() => !isGenerating && setPreviewOpen(true)}
                        >
                          Preview
                        </span>
                        <span
                          className="preview-link"
                          onClick={() => {
                            handleSendEmptyAnnouncementUpdate();

                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set(
                                "step",
                                CommonEnums.announcementPostTabs.select_template
                                  .key
                              );
                              return next;
                            });
                          }}
                        >
                          Change Template
                        </span>
                      </div>
                    </div>
                    <div
                      className={`activity-image-section ${getPreviewContainerClass(
                        postTypeFromUrl
                      )} ${isGenerating ? "loading" : ""}`}
                    >
                      {isGenerating ? (
                        <div className="post-preview-loading">
                          <div className="spinner" />
                          <h4>Your request is being processed.</h4>
                          <p>
                            We’re working on your request! Creating a
                            high-quality output can take a little while. We
                            appreciate your patience.
                          </p>
                          <p className="message">
                            Feel free to wait here, or browse other pages. We’ll
                            send you a notification when your request is
                            completed.
                          </p>
                        </div>
                      ) : (
                        <img
                          src={getPreviewImage()}
                          alt="Template Preview"
                          className="activity-thumbnail"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src =
                              "/images/image-video-generation-error-fallback-image-for-card.png";
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isLoading && (
                <>
                  <div className="footer-divider" />
                  <div className="footer-buttons">
                    <button
                      className="btn btn-secondary"
                      onClick={() => navigate(-1)}
                      disabled={isGenerating}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleCreateOnboardingPost}
                      disabled={isGenerating}
                    >
                      Confirm
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          <PreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            images={[getPreviewImage()]}
          />
          {step === CommonEnums.announcementPostTabs.configure_post.key && (
            <AnnouncementConfigurePost
              selectedPostTypeTitle={selectedPostTypeTitle}
            />
          )}
          {step === CommonEnums.announcementPostTabs.announcement_type.key && (
            <AnnouncementType />
          )}
          {step === CommonEnums.announcementPostTabs.select_template.key && (
            <SelectTemplatesList
              isAnnouncementPost={true}
              postTypeProp={searchParams.get("post_type") || "image_post"}
              listingTypeProp="sale"
              subscriptionTypeProp={
                searchParams.get("subscription_type") || "agent_marketing"
              }
              onSelectTemplate={(id) => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("template_id", id);
                  next.set("step", "create_post");
                  return next;
                });
              }}
            />
          )}
          {step === CommonEnums.announcementPostTabs.post_type.key && (
            <AnnouncementPostType
              selectedType={selectedPostType}
              onSelectType={(type) => setSelectedPostType(type)}
              onContinue={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set(
                    "step",
                    CommonEnums.announcementPostTabs.announcement_type.key
                  );
                  return next;
                });
              }}
            />
          )}

          <Modal
            open={processingModalOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setProcessingModalOpen(false);
              }
            }}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request
                    <span className="desktop-break">
                      <br />
                    </span>
                    Please wait...
                  </p>
                </div>
              </div>
            </div>
          </Modal>
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgencyCreateAnnouncementPost;
